import { createContext, useCallback, useContext, useReducer, useEffect } from 'react';
import type { User, OrgMembership, LoginCredentials, RegisterCredentials } from '../types';
import authService from '../services/auth';
import { isRefreshRecentlyFailed, hydrateAccessTokenIfFresh } from '../services/api';
import { followOrgSwitches, leaveForActiveOrg, setTabOrgId } from '../lib/orgGuard';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSwitchingOrg: boolean;
  error: string | null;
}

export type LoginResult =
  | { status: 'ok' }
  | {
      status: 'mfa_required';
      mfaTicket: string;
      availableMethods: { totp: boolean; email: boolean; passkey: boolean };
      rememberMe: boolean;
    };

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  completeMfaLogin: (user: User) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  switchOrg: (orgId: number) => Promise<void>; // resolves once AUTH_SUCCESS fires
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ORGS'; payload: OrgMembership[] }
  | { type: 'SWITCH_ORG_START' }
  | { type: 'SWITCH_ORG_ERROR' };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isSwitchingOrg: false,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        isSwitchingOrg: false,
        error: null,
      };

    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isSwitchingOrg: false,
        error: null,
      };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ORGS': {
      if (!state.user) return state;
      // Derive the active org from the returned list and update roles to match,
      // so that after a page refresh the displayed roles are always org-specific
      // and never bleed across organisations.
      const activeOrg = action.payload.find(o => o.is_active);
      return {
        ...state,
        user: {
          ...state.user,
          orgs: action.payload,
          roles: activeOrg?.roles ?? state.user.roles,
        },
      };
    }

    case 'SWITCH_ORG_START':
      return { ...state, isSwitchingOrg: true };

    case 'SWITCH_ORG_ERROR':
      return { ...state, isSwitchingOrg: false };

    default:
      return state;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, rawDispatch] = useReducer(authReducer, initialState);

  // The organisation this tab is showing rides on every request (lib/orgGuard).
  // It has to change before the render that follows, not in an effect after
  // it: the pages fetch from their own effects, which run before this one's.
  const dispatch = useCallback((action: AuthAction) => {
    if (action.type === 'AUTH_SUCCESS') setTabOrgId(action.payload.organisation_id);
    if (action.type === 'AUTH_LOGOUT' || action.type === 'AUTH_ERROR') setTabOrgId(null);
    rawDispatch(action);
  }, []);

  // Another tab in this browser switched organisation: follow it.
  useEffect(() => followOrgSwitches(), []);

  // Initialize auth state by attempting a silent token refresh.
  // The access token is in-memory only, so after a page reload we always need
  // a round-trip to /auth/refresh (uses the httpOnly refresh-token cookie).
  useEffect(() => {
    const initializeAuth = async () => {
      // If a refresh just failed (e.g. we arrived here via a 401 redirect),
      // don't hit the server again — go straight to the login screen.
      if (isRefreshRecentlyFailed()) {
        dispatch({ type: 'AUTH_LOGOUT' });
        return;
      }
      try {
        // On native, try to reuse a persisted access token from a previous
        // session. If it's still fresh we skip the /auth/refresh round-trip
        // entirely. If getCurrentUser still 401s, the interceptor will kick
        // off a refresh on demand.
        const hasFreshToken = hydrateAccessTokenIfFresh();
        if (!hasFreshToken) {
          await authService.refreshToken();
        }
        const currentUser = await authService.getCurrentUser();
        dispatch({ type: 'AUTH_SUCCESS', payload: currentUser });
        // Fetch org memberships in the background — don't block bootstrap.
        // The dashboard renders fine without the org list; it fills in once ready.
        authService.getMyOrgs()
          .then(orgs => dispatch({ type: 'SET_ORGS', payload: orgs }))
          .catch(() => { /* non-fatal: org list is optional on init */ });
      } catch {
        // No valid refresh token — user must log in
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    };

    initializeAuth();
  }, [dispatch]);

  const login = async (credentials: LoginCredentials): Promise<LoginResult> => {
    try {
      dispatch({ type: 'AUTH_START' });
      const result = await authService.login(credentials);

      if (result.status === 'mfa_required') {
        // Don't dispatch AUTH_SUCCESS yet — caller navigates to /mfa-challenge
        dispatch({ type: 'SET_LOADING', payload: false });
        return {
          status: 'mfa_required',
          mfaTicket: result.mfaTicket,
          availableMethods: result.availableMethods,
          rememberMe: result.rememberMe,
        };
      }

      dispatch({ type: 'AUTH_SUCCESS', payload: result.user });
      // Fetch org memberships in the background — don't block navigation
      authService.getMyOrgs()
        .then(orgs => dispatch({ type: 'SET_ORGS', payload: orgs }))
        .catch(() => { /* non-fatal */ });
      return { status: 'ok' };
    } catch (error: any) {
      dispatch({ type: 'AUTH_ERROR', payload: error.message || 'Login failed' });
      throw error;
    }
  };

  // Called by MfaChallenge after the server has already verified the 2nd factor
  // and persisted accessToken/user into storage. We just sync context state.
  const completeMfaLogin = async (user: User) => {
    dispatch({ type: 'AUTH_SUCCESS', payload: user });
    // Fetch org memberships in the background — don't block navigation
    authService.getMyOrgs()
      .then(orgs => dispatch({ type: 'SET_ORGS', payload: orgs }))
      .catch(() => { /* non-fatal */ });
  };

  const register = async (credentials: RegisterCredentials) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const authResponse = await authService.register(credentials);
      dispatch({ type: 'AUTH_SUCCESS', payload: authResponse.user });
    } catch (error: any) {
      dispatch({ type: 'AUTH_ERROR', payload: error.message || 'Registration failed' });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const refreshUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      // The account moved organisation under this tab: reload into it rather
      // than swap the user over a page still showing the old one's records.
      if (leaveForActiveOrg(user.organisation_id)) return;
      dispatch({ type: 'AUTH_SUCCESS', payload: user });
      // Memberships can change under a session (an accepted invitation).
      authService.getMyOrgs()
        .then(orgs => dispatch({ type: 'SET_ORGS', payload: orgs }))
        .catch(() => { /* non-fatal */ });
    } catch (error: any) {
      dispatch({ type: 'AUTH_ERROR', payload: error.message || 'Failed to refresh user' });
      throw error;
    }
  };

  const switchOrg = async (orgId: number) => {
    dispatch({ type: 'SWITCH_ORG_START' });
    try {
      // switchOrg updates the DB then calls getCurrentUser() — the auth middleware
      // reads active_organisation_id from DB on every request, so no new token needed.
      const user = await authService.switchOrg(orgId);
      dispatch({ type: 'AUTH_SUCCESS', payload: user });
      // Re-fetch org list in the background so is_active flags stay accurate
      authService.getMyOrgs().then(orgs => {
        dispatch({ type: 'SET_ORGS', payload: orgs });
      }).catch(() => {});
    } catch (error: any) {
      dispatch({ type: 'SWITCH_ORG_ERROR' });
      throw error;
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    completeMfaLogin,
    register,
    logout,
    clearError,
    refreshUser,
    switchOrg,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
